import classnames from 'classnames'
import * as React from 'react'
import { useInRouterContext } from 'react-router-dom'
import * as Reakit from 'reakit/Popover'

import { isMobile } from '../../../app/shared/isMobile'
import { getExtendedDialogState } from '../dialog'
import { ListItem } from '../list'
import { PopoverStateReturn, usePopoverState } from '../popover'
import { Label } from '../typography'
import { useSelect } from './hooks'
import { NativeSelect, NativeSelectInRouterContext } from './native-select'
import { SelectButton, SelectButtonProps } from './select-button'
import { SelectList, SelectListProps } from './select-list'
import { SelectItem } from './types'

/**
 * All non-select props get passed to the button element, these are the supported props
 */
type ButtonPassthroughProps = Omit<SelectButtonProps, 'label' | 'name' | 'value' | 'onChange' | 'state'>

export interface SelectProps<ItemType, ValueType, Name> extends ButtonPassthroughProps {
  /**
   * Optional popover state settings
   */
  popoverStateSettings?: Reakit.PopoverInitialState

  /**
   * Name of the select, will be included in `onChange` event
   */
  name?: Name

  /**
   * Current selected value
   */
  value?: ValueType

  /**
   * List of items to display in the select
   */
  items: ItemType[]

  /**
   * Callback when selected item changes
   */
  onChange?: (item: ItemType, name: Name) => void

  /**
   * Label to display above the select
   */
  label?: React.ReactNode

  /**
   * Text for the button (usually the selected value). Falls back to placeholder if not set
   */
  buttonContent?: React.ReactNode

  /**
   * Placeholder when no value is selected
   */
  placeholder?: string

  /**
   * Additional props to the popover list
   */
  listProps?: Omit<SelectListProps, 'items' | 'selectedIndex' | 'onChange' | 'state'>

  /**
   * Optional label className override
   */
  labelClassName?: string

  /**
   * Optional label layout switch
   */
  isHorizontalLabel?: boolean

  /**
   * Optional label title className override
   */
  labelTitleClassName?: string

  /**
   * Should use native select element on mobile devices
   *
   * @default true
   */
  isNativeMobileSelect?: boolean

  /**
   * Should use native select element
   *
   * @default false
   */
  isAlwaysNativeSelect?: boolean

  /**
   * Set the popover to height to be limited by the viewport instead of having full height
   *
   * @default false
   */
  hasPopoverViewportHeightLimit?: boolean

  /**
   * Set the popover max-width to be limited by the trigger button
   */
  hasPopoverButtonWidthLimit?: boolean

  /**
   * Triggered when popover state visibility/animating changes
   */
  onIsInteractingChange?: (isInteracting: boolean) => void
}

export function Select<ValueType, ItemType extends SelectItem<ValueType>, Name = never>({
  children,
  popoverStateSettings,
  onIsInteractingChange,
  ...props
}: SelectProps<ItemType, ValueType, Name>) {
  const popoverState = usePopoverState(popoverStateSettings)
  const { isFullyClosed } = getExtendedDialogState(popoverState)

  React.useEffect(() => {
    onIsInteractingChange?.(!isFullyClosed)
  }, [onIsInteractingChange, isFullyClosed])

  return (
    <StatelessSelect {...props} popoverState={popoverState}>
      {children}
    </StatelessSelect>
  )
}

export interface StatelessSelectProps<ItemType, ValueType, Name> extends SelectProps<ItemType, ValueType, Name> {
  /**
   * Popover state for handling list in popover
   */
  popoverState: PopoverStateReturn
}

export function StatelessSelect<ValueType, ItemType extends SelectItem<ValueType>, Name = never>({
  // Select
  name,
  label,
  isHorizontalLabel,
  value,
  items = [],
  onChange,
  labelClassName,
  labelTitleClassName,
  'aria-label': ariaLabel,
  // Button
  theme,
  buttonContent,
  placeholder = 'Select value',
  // Popover
  popoverState,
  listProps,
  isNativeMobileSelect = true,
  isAlwaysNativeSelect = false,
  hasPopoverViewportHeightLimit = false,
  hasPopoverButtonWidthLimit = true,
  children,
  ...props
}: StatelessSelectProps<ItemType, ValueType, Name>) {
  const { selectedIndex, onItemChange } = useSelect(popoverState, value, items, (item) => onChange?.(item, name!))
  const showMobileSelect = isNativeMobileSelect && isMobile()
  const isNativeSelect = isAlwaysNativeSelect || showMobileSelect

  /** Items can have `href` and be router Links. We need a way to account for this in native select */
  const isInRouterContext = useInRouterContext()
  const NativeSelectComponent = isInRouterContext ? NativeSelectInRouterContext : NativeSelect

  return (
    <Label
      title={label}
      titleClassName={labelTitleClassName}
      isHorizontal={isHorizontalLabel}
      className={classnames(labelClassName, { relative: isNativeSelect })}
    >
      <SelectButton {...props} state={popoverState} theme={theme} active={props.active ?? popoverState.visible}>
        {buttonContent ?? items[selectedIndex!]?.label ?? placeholder}
      </SelectButton>
      {isNativeSelect ? (
        <NativeSelectComponent
          /**
           * The key attribute fixes error on iOS Safari, where the placeholder option wouldn’t get
           * hidden in some cases
           */
          key={selectedIndex}
          items={items}
          onChange={onItemChange}
          value={selectedIndex}
          placeholder={placeholder}
        />
      ) : (
        <SelectList
          state={popoverState}
          selectedIndex={selectedIndex}
          theme={theme === 'light' || theme === 'dark' ? theme : undefined}
          hasViewportHeightLimit={hasPopoverViewportHeightLimit}
          hasButtonWidthLimit={hasPopoverButtonWidthLimit}
          onChange={onItemChange}
          aria-label={ariaLabel}
          {...listProps}
        >
          {children}
        </SelectList>
      )}
    </Label>
  )
}

export function SelectPlaceholder<ValueType, ItemType extends SelectItem<ValueType>, Name = never>({
  // Select
  label,
  labelClassName,
  isHorizontalLabel,
  labelTitleClassName,
  // Button
  theme,
  buttonContent,
  placeholder = 'Select value',
  ...props
}: Omit<
  StatelessSelectProps<ItemType, ValueType, Name>,
  | 'name'
  | 'aria-label'
  | 'value'
  | 'items'
  | 'onChange'
  | 'children'
  | 'listProps'
  | 'popoverState'
  | 'isNativeMobileSelect'
  | 'hasPopoverViewportHeightLimit'
  | 'hasPopoverButtonWidthLimit'
>) {
  return (
    <Label
      title={label}
      titleClassName={labelTitleClassName}
      className={labelClassName}
      isHorizontal={isHorizontalLabel}
    >
      <SelectButton {...props} isPopoverTrigger={false} theme={theme} active={props.active}>
        {buttonContent ?? placeholder}
      </SelectButton>
    </Label>
  )
}

export const SelectOption = ListItem
